import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import JSZip from 'jszip';
import AppLayout from '../../components/AppLayout';
import Button from '../../components/ui/Button';
import CutListForm from '../../components/cutlist/CutListForm';
import CutListPreview from '../../components/cutlist/CutListPreview';
import { useJobContext } from '../../contexts/CutlistJobContext';
import { generateCutListPDF } from '../../pdf/cutListPDF';
import { generateGlassOrderPDF } from '../../pdf/glassOrderPDF';
import { generateFlexScreenOrderPDF } from '../../pdf/flexScreenOrderPDF';
import { generateShopOrderPDF } from '../../pdf/shopOrderPDF';
import { updateJob as updateFirebaseJob, getJob } from '../../services/cutlist-jobs';
import { saveDocument, getDocumentStatusMap } from '../../services/cutlist-documents';
import {
  LayoutDashboard, FileText, Ruler, Factory, Plus, Link2, ArrowLeft,
  Download, Printer, X, Loader2, Pencil, Archive, LayoutGrid, AppWindow, Check,
} from 'lucide-react';

const INK    = '#2B2620';
const WALNUT = '#8B4513';
const MOSS   = '#3B6B44';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';

const FONT_SANS = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

/* ═══════════════════════════════════════════════════════════════════════
   PDF PREVIEW MODAL — proper React component, no render-time side effects
   ═══════════════════════════════════════════════════════════════════════ */
function PdfPreviewModal({ previewPdf, onClose }) {
  const [objectUrl, setObjectUrl] = useState(null);

  useEffect(function() {
    if (!previewPdf) { setObjectUrl(null); return; }
    var raw = previewPdf.blob;
    if (!raw) { setObjectUrl(null); return; }

    var blob;
    if (raw instanceof Blob) {
      blob = raw;
    } else if (raw instanceof ArrayBuffer || raw instanceof Uint8Array) {
      blob = new Blob([raw], { type: 'application/pdf' });
    } else if (typeof raw === 'string') {
      blob = new Blob([raw], { type: 'application/pdf' });
    } else if (raw && typeof raw.output === 'function') {
      blob = raw.output('blob');
    } else {
      try { blob = new Blob([raw], { type: 'application/pdf' }); }
      catch(e) { console.error('Cannot create PDF blob:', e); setObjectUrl(null); return; }
    }

    var url = URL.createObjectURL(blob);
    setObjectUrl(url);
    return function() { URL.revokeObjectURL(url); };
  }, [previewPdf]);

  if (!previewPdf || !objectUrl) return null;

  var isLandscape = previewPdf.landscape === true;
  var viewParam = isLandscape
    ? '#toolbar=0&navpanes=0&view=FitH'
    : '#toolbar=0&navpanes=0&view=FitV';

  function handleDownload() {
    var a = document.createElement('a');
    a.href = objectUrl;
    a.download = previewPdf.filename || 'document.pdf';
    a.click();
  }

  function handlePrint() {
    var f = document.createElement('iframe');
    f.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:none;';
    f.src = objectUrl;
    document.body.appendChild(f);
    f.onload = function() {
      setTimeout(function() {
        f.contentWindow.print();
        setTimeout(function() { document.body.removeChild(f); }, 1000);
      }, 500);
    };
  }

  return (
    <div onClick={function(e){ if (e.target === e.currentTarget) onClose(); }}
      style={{position:'fixed',inset:0,zIndex:9999,background:'rgba(0,0,0,0.75)',display:'flex',flexDirection:'column',backdropFilter:'blur(4px)'}}>
      <div style={{background:PAPER,padding:'12px 20px',display:'flex',alignItems:'center',justifyContent:'space-between',borderBottom:'1px solid rgba(139,69,19,0.1)',flexShrink:0,fontFamily:FONT_SANS}}>
        <div>
          <div style={{fontSize:'16px',fontWeight:'700',color:INK}}>{previewPdf.title}</div>
          <div style={{fontSize:'11px',color:STONE,marginTop:'1px'}}>{previewPdf.filename}</div>
        </div>
        <div style={{display:'flex',gap:'8px'}}>
          <button onClick={handlePrint} style={{display:'flex',alignItems:'center',gap:'6px',padding:'8px 18px',borderRadius:'8px',border:'none',background:INK,color:'white',fontSize:'13px',fontWeight:'700',cursor:'pointer',fontFamily:FONT_SANS}}>
            <Printer size={14} strokeWidth={2} /> Print
          </button>
          <button onClick={handleDownload} style={{display:'flex',alignItems:'center',gap:'6px',padding:'8px 18px',borderRadius:'8px',border:'none',background:WALNUT,color:'white',fontSize:'13px',fontWeight:'700',cursor:'pointer',fontFamily:FONT_SANS}}>
            <Download size={14} strokeWidth={2} /> Download
          </button>
          <button onClick={onClose} style={{display:'flex',alignItems:'center',gap:'6px',padding:'8px 18px',borderRadius:'8px',border:'1px solid rgba(139,69,19,0.15)',background:PAPER,color:STONE,fontSize:'13px',fontWeight:'600',cursor:'pointer',fontFamily:FONT_SANS}}>
            <X size={14} strokeWidth={2} /> Close
          </button>
        </div>
      </div>
      <div style={{flex:1,overflow:'hidden',background:'#525659'}}>
        <iframe
          key={objectUrl}
          src={objectUrl + viewParam}
          style={{width:'100%',height:'100%',border:'none'}}
          title="PDF Preview"
        />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   MAIN CUT LIST COMPONENT
   ═══════════════════════════════════════════════════════════════════════ */
export default function CutList() {
  const [view, setView]                 = useState('form');
  const [downloading, setDownloading]   = useState(false);
  const [previewPdf, setPreviewPdf]     = useState(null);
  const [autoJumped, setAutoJumped]     = useState(false);
  const [docStatusMap, setDocStatusMap] = useState({});

  const navigate  = useNavigate();

  const {
    job, calculatedWindows, errors,
    updateJob, addWindow, updateWindow,
    removeWindow, duplicateWindow, calculateAll, clearJob,
    loadFromJob, linkedJobId,
  } = useJobContext();

  // Load job from URL
  const { id: urlJobId } = useParams();
  useEffect(() => {
    if (urlJobId) {
      getJob(urlJobId).then(data => {
        if (data) loadFromJob({ ...data, id: urlJobId });
        else navigate('/cutlist', { replace: true });
      }).catch(() => navigate('/cutlist', { replace: true }));
    } else {
      navigate('/cutlist', { replace: true });
    }
  }, [urlJobId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Document generation status — drives the "Shop Order" step checkmark below,
  // so it reflects real generation history instead of always looking pending.
  useEffect(() => {
    if (!linkedJobId) { setDocStatusMap({}); return; }
    getDocumentStatusMap(linkedJobId).then(setDocStatusMap).catch(() => setDocStatusMap({}));
  }, [linkedJobId]);

  // Auto-jump to preview once
  useEffect(() => {
    if (calculatedWindows.length > 0 && view === 'form' && !autoJumped) {
      setView('preview');
      setAutoJumped(true);
    }
  }, [calculatedWindows.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helper: merge Firebase job fields (address, phone, etc.) ──────────
  async function getJobWithFirebaseFields() {
    let linkedFirebaseJob = {};
    if (linkedJobId) {
      try {
        const fbJob = await getJob(linkedJobId);
        if (fbJob) {
          linkedFirebaseJob = fbJob;
          // Also fetch from main jobs collection for full address/phone
          if (fbJob.twpJobId) {
            try {
              const { getDoc, doc } = await import('firebase/firestore');
              const { db } = await import('../../services/firebase-init');
              const mainJobSnap = await getDoc(doc(db, 'jobs', fbJob.twpJobId));
              if (mainJobSnap.exists()) {
                const mainJob = mainJobSnap.data();
                // Only pull contact fields from main job
                if (mainJob.address)     linkedFirebaseJob.address     = mainJob.address;
                if (mainJob.phoneNumber) linkedFirebaseJob.phoneNumber = mainJob.phoneNumber;
              }
            } catch(e) { console.warn('Could not load main job fields:', e); }
          }
        }
      } catch(e) { console.warn('Could not load linked job fields:', e); }
    }
    return { ...linkedFirebaseJob, ...job, id: linkedJobId };
  }

  // ── Calculate + save ──────────────────────────────────────────────────
  async function handleCalculate() {
    const results = calculateAll();
    if (results.length > 0) {
      setAutoJumped(true);
      setView('preview');
      const jobWithIds = await getJobWithFirebaseFields();
      try {
        const clBlob = await generateCutListPDF(jobWithIds, results, true);
        const glBlob = generateGlassOrderPDF(jobWithIds, results, true);
        const shBlob = generateShopOrderPDF(jobWithIds, results, true);
        const flBlob = generateFlexScreenOrderPDF(jobWithIds, results, true);
        console.log('✅ PDFs generated successfully');
        if (linkedJobId) {
          const woNum = (job.woNumber || 'DRAFT').replace(/\s+/g, '_');
          const { uploadJobPDF, savePDFUrlToJob } = await import('../../services/pdf-storage');
          const uploadPromises = [
            uploadJobPDF(woNum, 'cutlist', clBlob).then(url => savePDFUrlToJob(job.twpJobId || null, linkedJobId, 'cutlist', url)),
            uploadJobPDF(woNum, 'glassorder', glBlob).then(url => savePDFUrlToJob(job.twpJobId || null, linkedJobId, 'glassorder', url)),
            uploadJobPDF(woNum, 'shoporder', shBlob).then(url => savePDFUrlToJob(job.twpJobId || null, linkedJobId, 'shoporder', url)),
          ];
          if (flBlob) {
            uploadPromises.push(uploadJobPDF(woNum, 'flexscreen', flBlob).then(url => savePDFUrlToJob(job.twpJobId || null, linkedJobId, 'flexscreen', url)));
          }
          await updateFirebaseJob(linkedJobId, { windows: job.windows, windowCount: results.length, calculatedWindows: results, status: 'in_production' });
          await Promise.all([saveDocument(linkedJobId, 'cutlist'), saveDocument(linkedJobId, 'glass'), saveDocument(linkedJobId, 'flexscreen'), saveDocument(linkedJobId, 'shoporder')]);
          getDocumentStatusMap(linkedJobId).then(setDocStatusMap).catch(() => {});
          Promise.all(uploadPromises).then(() => console.log('✅ PDFs uploaded')).catch(e => console.warn('⚠️ PDF upload failed:', e));
        }
      } catch (e) { console.error('❌ PDF error:', e); }
    }
  }

  // ── Download ZIP ──────────────────────────────────────────────────────
  async function handleDownloadAll() {
    if (!calculatedWindows.length) return;
    setDownloading(true);
    try {
      const folderName = `${(job.customerName || 'Job').replace(/\s+/g, '_')}_WO${job.woNumber || 'DRAFT'}`;
      const zip = new JSZip();
      const folder = zip.folder(folderName);
      const jobWithIds = await getJobWithFirebaseFields();
      const cutListBlob = await generateCutListPDF(jobWithIds, calculatedWindows, true);
      const glassBlob = generateGlassOrderPDF(jobWithIds, calculatedWindows, true);
      const shopBlob = generateShopOrderPDF(jobWithIds, calculatedWindows, true);
      const flexBlob = generateFlexScreenOrderPDF(jobWithIds, calculatedWindows, true);
      folder.file(`${folderName}_CutList.pdf`, cutListBlob);
      folder.file(`${folderName}_GlassOrder.pdf`, glassBlob);
      folder.file(`${folderName}_ShopOrder.pdf`, shopBlob);
      if (flexBlob) folder.file(`${folderName}_FlexScreen.pdf`, flexBlob);
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a'); a.href = url; a.download = `${folderName}.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error('ZIP failed:', e); }
    finally { setDownloading(false); }
  }

  function goToShopOrder() { navigate(`/cutlist/${urlJobId}/shoporder`); }

  // ── Preview helpers ───────────────────────────────────────────────────
  var jobPrefix = (job.customerName || 'Job').replace(/\s+/g, '_');

  async function previewCutList() {
    try {
      const jobWithIds = await getJobWithFirebaseFields();
      var b = await generateCutListPDF(jobWithIds, calculatedWindows, true);
      if (b) setPreviewPdf({ blob: b, title: 'Cut List', filename: jobPrefix + '_CutList.pdf', landscape: false });
    } catch(e) { console.error('Cut list preview error:', e); }
  }
  async function previewGlass() {
    try {
      const jobWithIds = await getJobWithFirebaseFields();
      var b = await generateGlassOrderPDF(jobWithIds, calculatedWindows, true);
      if (b) setPreviewPdf({ blob: b, title: 'Glass Order', filename: jobPrefix + '_GlassOrder.pdf', landscape: true });
      else alert('No glass order data.');
    } catch(e) { console.error('Glass preview error:', e); }
  }
  async function previewFlexScreen() {
    try {
      const jobWithIds = await getJobWithFirebaseFields();
      var b = await generateFlexScreenOrderPDF(jobWithIds, calculatedWindows, true);
      if (b) setPreviewPdf({ blob: b, title: 'FlexScreen Order', filename: jobPrefix + '_FlexScreen.pdf', landscape: true });
      else alert('No flex screens found.');
    } catch(e) { console.error('FlexScreen preview error:', e); }
  }
  async function previewShopOrder() {
    try {
      const jobWithIds = await getJobWithFirebaseFields();
      var b = await generateShopOrderPDF(jobWithIds, calculatedWindows, true);
      if (b) setPreviewPdf({ blob: b, title: 'Shop Order', filename: jobPrefix + '_ShopOrder.pdf', landscape: true });
      else alert('No shop order data.');
    } catch(e) { console.error('Shop order preview error:', e); }
  }

  const STEPS = [
    { id: 'dashboard', step: 1, Icon: LayoutDashboard, label: 'Job Dashboard', type: 'route', path: '/cutlist' },
    { id: 'form',      step: 2, Icon: FileText,        label: 'Job Entry',     type: 'view' },
    { id: 'preview',   step: 3, Icon: Ruler,           label: 'Cut List',      type: 'view', disabled: calculatedWindows.length === 0 },
    { id: 'shoporder', step: 4, Icon: Factory,         label: 'Shop Order',    type: 'route', disabled: calculatedWindows.length === 0, onClick: goToShopOrder },
  ];
  const currentStep = view === 'form' ? 2 : 3;

  const breadcrumb = 'Dashboard › Cutlist Dashboard › ' + (job.customerName || 'Job Entry');

  return (
    <AppLayout
      title={view === 'form' ? 'Job Entry' : 'Cut List Preview'}
      breadcrumb={breadcrumb}
      topBarActions={
        view === 'preview' && calculatedWindows.length > 0 ? (
          <>
            <Button size="small" onClick={handleDownloadAll} disabled={downloading}>
              {downloading ? <Loader2 size={14} strokeWidth={2} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Archive size={14} strokeWidth={2} />}
              {downloading ? 'Zipping…' : 'All 4 PDFs'}
            </Button>
            <Button size="small" onClick={previewCutList}><FileText size={14} strokeWidth={2} /> Cut List</Button>
            <Button size="small" onClick={previewGlass}><LayoutGrid size={14} strokeWidth={2} /> Glass</Button>
            <Button size="small" onClick={previewFlexScreen}><AppWindow size={14} strokeWidth={2} /> FlexScreen</Button>
            <Button size="small" onClick={previewShopOrder}><Factory size={14} strokeWidth={2} /> Shop Order</Button>
            <Button size="small" onClick={() => setView('form')}><Pencil size={14} strokeWidth={2} /> Edit Windows</Button>
          </>
        ) : null
      }
    >
      <div style={{ maxWidth: '1200px', width: '100%', margin: '0 auto', boxSizing: 'border-box', fontFamily: FONT_SANS }}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

        {/* Top row: back link + New Job + linked job chip */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '18px', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/cutlist')}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: WALNUT, fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: FONT_SANS, padding: 0 }}
          >
            <ArrowLeft size={14} strokeWidth={2} /> Back to Cutlist Dashboard
          </button>

          <div style={{ flex: 1 }} />

          {linkedJobId && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 12px', borderRadius: '20px', background: 'rgba(59,107,68,0.08)', border: '1px solid rgba(59,107,68,0.2)', fontSize: '11px', fontWeight: '600', color: MOSS }}>
              <Link2 size={12} strokeWidth={2} /> {job.customerName || 'Linked job'}{job.woNumber ? ` — WO# ${job.woNumber}` : ''}
            </div>
          )}

          <button
            onClick={() => { clearJob(); navigate('/cutlist'); }}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px', background: 'transparent', border: '1px solid rgba(139,69,19,0.2)', borderRadius: '8px', color: STONE, fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: FONT_SANS }}
          >
            <Plus size={13} strokeWidth={2} /> New Job
          </button>
        </div>

        {/* Step progress bar */}
        <div style={{ background: PAPER, borderRadius: '14px', border: '1px solid rgba(139,69,19,0.1)', boxShadow: '0 2px 12px rgba(139,69,19,0.06)', padding: '14px 20px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '4px', overflowX: 'auto' }}>
          {STEPS.map((s, i) => {
            const isActive   = s.type === 'view' && view === s.id;
            const isDone     = s.step < currentStep
              || (s.step === 3 && calculatedWindows.length > 0 && s.id !== view)
              || (s.id === 'shoporder' && docStatusMap.shoporder?.status === 'generated');
            const isDisabled = !!s.disabled;
            function handleClick() {
              if (isDisabled) return;
              if (s.onClick) { s.onClick(); return; }
              if (s.type === 'route') { navigate(s.path); return; }
              setView(s.id);
            }
            const color = isActive ? WALNUT : isDone ? MOSS : isDisabled ? STONE : INK;
            return (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', flex: i < STEPS.length - 1 ? 1 : 'none', minWidth: 'fit-content' }}>
                <button
                  onClick={handleClick}
                  disabled={isDisabled}
                  title={isDisabled ? 'Calculate a cut list first' : s.label}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '7px 12px', borderRadius: '20px', border: 'none',
                    background: isActive ? WALNUT : 'transparent',
                    color: isActive ? 'white' : color,
                    fontSize: '12.5px', fontWeight: isActive ? '700' : '600',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    opacity: isDisabled ? 0.5 : 1,
                    fontFamily: FONT_SANS, whiteSpace: 'nowrap',
                  }}
                >
                  {isDone ? <Check size={14} strokeWidth={2.5} /> : <s.Icon size={14} strokeWidth={2} />}
                  {s.label}
                  {s.id === 'preview' && calculatedWindows.length > 0 && (
                    <span style={{ background: isActive ? 'rgba(255,255,255,0.3)' : `${MOSS}18`, color: isActive ? 'white' : MOSS, fontSize: '10px', padding: '1px 6px', borderRadius: '999px', fontWeight: '700' }}>
                      {calculatedWindows.length}
                    </span>
                  )}
                </button>
                {i < STEPS.length - 1 && (
                  <div style={{ flex: 1, height: '1px', minWidth: '16px', background: isDone ? MOSS : 'rgba(139,69,19,0.12)' }} />
                )}
              </div>
            );
          })}
        </div>

        {/* Page content */}
        {view === 'form' && (
          <CutListForm
            job={job}
            onUpdateJob={updateJob}
            onAddWindow={addWindow}
            onUpdateWindow={updateWindow}
            onRemoveWindow={removeWindow}
            onDuplicateWindow={duplicateWindow}
            onCalculate={handleCalculate}
            errors={errors}
          />
        )}
        {view === 'preview' && (
          <CutListPreview job={job} calculatedWindows={calculatedWindows} />
        )}
      </div>

      {/* ── PDF Preview Modal (proper component) ──────────────────────────── */}
      <PdfPreviewModal previewPdf={previewPdf} onClose={() => setPreviewPdf(null)} />
    </AppLayout>
  );
}
